import { useEffect, useState } from 'react';
import { formatBytes } from '../lib/emitter.ts';
import { downloadJournal, downloadLocalFile, exportName, listLocalTakes, saveTakeToFolder, type LocalTake } from '../lib/local-copies.ts';
import { canUseFolders, saveFolder } from '../lib/save-folder.ts';
import { localFiles, storageEstimate } from '../lib/storage.ts';
import { sharedUploads } from '../lib/studio.ts';
import { AppBar, ConfirmDialog, Icon, useInterval, useObservable, useTitle } from './common.tsx';

/**
 * "On this computer": every recording kept in this browser, where they are, and getting them out: copy a take
 * into a folder (or have each take copied automatically), download single files, upload a keep-local take after
 * all, or remove a take from this computer.
 */
export function LocalPage() {
  useTitle('On this computer');
  const storage = localFiles();
  const folder = saveFolder();
  const { uploader } = sharedUploads();
  useObservable(folder);
  useObservable(uploader);
  const [takes, setTakes] = useState<LocalTake[] | null>(null);
  const [space, setSpace] = useState<Awaited<ReturnType<typeof storageEstimate>> | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [removing, setRemoving] = useState<LocalTake | null>(null);

  const refresh = () => {
    void listLocalTakes(storage)
      .then(setTakes)
      .catch(() => setTakes([]));
    void storageEstimate().then(setSpace);
  };
  useEffect(refresh, []);
  useInterval(refresh, 10_000);

  const run = async (id: string, fn: () => Promise<string | void>) => {
    setBusy(id);
    setNote(null);
    try {
      const done = await fn();
      if (done) setNote({ ok: true, text: done });
    } catch (e) {
      if ((e as Error).name !== 'AbortError') setNote({ ok: false, text: (e as Error).message });
    } finally {
      setBusy(null);
      refresh();
    }
  };

  const used = takes?.reduce((a, t) => a + t.files.reduce((b, f) => b + f.size, 0), 0) ?? 0;

  return (
    <div className="page">
      <AppBar />
      <main className="account-page local-page">
        <div className="col" style={{ gap: 4 }}>
          <span className="eyebrow">On this computer</span>
          <h1>Your recordings here</h1>
        </div>

        <section className="card">
          <h2>Where they are</h2>
          <p className="muted">
            Wordstow records into this browser’s own storage first. That’s what keeps a recording safe if the browser
            crashes mid-take, but it isn’t a folder you can open in Finder or Explorer: use the buttons below to copy
            takes out. Clearing this site’s data in the browser deletes them.
          </p>
          <div className="kv-row">
            <span>
              {takes == null ? 'Looking…' : `${takes.length} take${takes.length === 1 ? '' : 's'} · ${formatBytes(used)}`}
              {space && Number.isFinite(space.free) && <span className="faint"> · {formatBytes(space.free)} free for recordings</span>}
            </span>
            {space && (
              <span className={`pill ${space.persisted ? 'ok' : ''}`} title={space.persisted ? 'The browser won’t clear these to make space' : 'The browser may clear these if the disk gets very full'}>
                {space.persisted ? 'Kept safe from browser clean-up' : 'May be cleared if the disk fills'}
              </span>
            )}
          </div>
        </section>

        <section className="card">
          <h2>Save each take to a folder</h2>
          {canUseFolders() ? (
            <>
              <p className="muted">
                Every take you record in this browser is copied into a folder of your choice when it ends, with the take’s timeline
                for hosts and producers, ready to open in DaVinci Resolve, Final Cut Pro or Premiere.
              </p>
              {folder.handle ? (
                <div className="kv-row">
                  <span>
                    <Icon name="folder" size={15} /> <strong>{folder.handle.name}</strong>{' '}
                    <span className="faint small">
                      {folder.access === 'granted' ? '· allowed' : '· the browser will ask again before writing to it'}
                    </span>
                  </span>
                  <span className="row" style={{ gap: 6 }}>
                    {folder.access !== 'granted' && (
                      <button className="sm primary" onClick={() => void folder.allow()}>
                        Allow
                      </button>
                    )}
                    <button className="sm" onClick={() => void run('folder', async () => void (await folder.choose()))}>
                      Change…
                    </button>
                    <button className="sm ghost" onClick={() => void folder.forget()}>
                      Stop
                    </button>
                  </span>
                </div>
              ) : (
                <button className="primary" style={{ alignSelf: 'flex-start' }} onClick={() => void run('folder', async () => void (await folder.choose()))}>
                  <Icon name="folder" size={15} /> Choose a folder…
                </button>
              )}
            </>
          ) : (
            <p className="muted">
              This browser can’t write to a folder (Chrome and Edge can). Download each take’s files below; your browser puts them in
              its downloads folder.
            </p>
          )}
        </section>

        {note && (
          <div className={`alert ${note.ok ? 'info' : 'bad'}`}>
            <Icon name={note.ok ? 'check' : 'warn'} size={15} /> {note.text}
          </div>
        )}

        {takes != null && !takes.length && (
          <div className="card empty" style={{ border: '1px solid var(--line)' }}>
            <Icon name="film" size={26} />
            <strong style={{ color: 'var(--text)' }}>No recordings on this computer</strong>
            Takes you record in this browser appear here, and stay until you remove them.
          </div>
        )}

        {takes?.map((t) => {
          const total = t.files.reduce((a, f) => a + f.size, 0);
          const live = t.files.some((f) => f.recording);
          const jobs = uploader.jobs.filter((j) => j.takeId === t.journal.takeId && t.files.some((f) => f.entry.localName === j.localName));
          const kept = jobs.filter((j) => j.keepLocal && !j.done);
          const uploaded = jobs.length > 0 && jobs.every((j) => j.done);
          const saved = folder.saved[t.journalName];
          return (
            <section key={t.journalName} className="card local-take-card">
              <div className="row between wrap" style={{ gap: 10 }}>
                <div className="col" style={{ gap: 2 }}>
                  <h2>
                    {t.journal.studioName || 'Studio'} · Take {t.journal.takeNumber}
                  </h2>
                  <span className="faint small">
                    {new Date(t.journal.createdAt).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })} ·{' '}
                    {t.journal.participantName} · {formatBytes(total)}
                  </span>
                </div>
                <div className="row wrap" style={{ gap: 6 }}>
                  {live ? (
                    <span className="pill rec">
                      <span className="dot pulse" /> Recording
                    </span>
                  ) : kept.length ? (
                    <span className="pill" title="This studio keeps recordings on each person’s computer">
                      Kept here
                    </span>
                  ) : uploaded ? (
                    <span className="pill ok">
                      <Icon name="check" size={12} /> Uploaded
                    </span>
                  ) : null}
                  {saved && (
                    <span className="pill ok" title={`Copied ${new Date(saved.at).toLocaleString()}`}>
                      <Icon name="folder" size={12} /> In {saved.folder}
                    </span>
                  )}
                </div>
              </div>
              <div className="col" style={{ gap: 6 }}>
                {t.files.map((f) => (
                  <div key={f.entry.localName} className="row between small">
                    <span className="ellipsis">
                      {f.entry.label} <span className="faint">· {f.entry.ext.toUpperCase()} · {formatBytes(f.size)}</span>
                    </span>
                    <button
                      className="ghost sm icon"
                      disabled={f.recording || busy != null}
                      title={f.recording ? 'Still recording' : 'Download this file'}
                      aria-label={`Download ${f.entry.label}`}
                      onClick={() => run(f.entry.localName, () => downloadLocalFile(storage, f.entry.localName, exportName(t, f.entry)))}
                    >
                      <Icon name="download" size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="row wrap" style={{ gap: 6 }}>
                {canUseFolders() && (
                  <button
                    className="sm"
                    disabled={busy != null || live}
                    onClick={() =>
                      run(t.journalName, async () => {
                        const root = folder.handle && (await folder.allow()) ? folder.handle : undefined;
                        const name = await saveTakeToFolder(storage, t, { root });
                        return `Copied take ${t.journal.takeNumber} to “${name}”.`;
                      })
                    }
                  >
                    {busy === t.journalName ? <span className="spinner" style={{ width: 12, height: 12, borderWidth: 1.5 }} /> : <Icon name="folder" size={14} />}{' '}
                    {folder.handle ? `Copy to ${folder.handle.name}` : 'Copy to a folder…'}
                  </button>
                )}
                {kept.length > 0 && (
                  <button className="sm" onClick={() => uploader.uploadKept(t.journal.takeId)} title="Send this take’s files to the studio after all">
                    <Icon name="upload" size={14} /> Upload to the studio
                  </button>
                )}
                <button className="ghost sm" onClick={() => downloadJournal(t)} title="This computer's record of the take: files, sync marks, events">
                  Journal (.json)
                </button>
                <span className="grow" />
                <button className="ghost sm danger" disabled={live || busy != null} onClick={() => setRemoving(t)}>
                  <Icon name="trash" size={14} /> Remove from this computer
                </button>
              </div>
            </section>
          );
        })}
      </main>
      {removing && (
        <RemoveDialog
          take={removing}
          safe={
            !!folder.saved[removing.journalName] ||
            (() => {
              const jobs = uploader.jobs.filter((j) => removing.files.some((f) => f.entry.localName === j.localName));
              return jobs.length > 0 && jobs.every((j) => j.done);
            })()
          }
          onClose={() => setRemoving(null)}
          onConfirm={async () => {
            const names = removing.files.map((f) => f.entry.localName);
            for (const n of names) await storage.delete(n).catch(() => {});
            await storage.delete(removing.journalName).catch(() => {});
            uploader.forget(names);
            setRemoving(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

function RemoveDialog({ take, safe, onClose, onConfirm }: { take: LocalTake; safe: boolean; onClose: () => void; onConfirm: () => Promise<void> }) {
  const total = take.files.reduce((a, f) => a + f.size, 0);
  return (
    <ConfirmDialog
      title={`Remove take ${take.journal.takeNumber} from this computer?`}
      confirmLabel="Remove"
      typeToConfirm={safe ? undefined : 'remove'}
      onClose={onClose}
      onConfirm={onConfirm}
    >
      <p>
        Its {take.files.length} file{take.files.length === 1 ? '' : 's'} ({formatBytes(total)}) are deleted from this browser’s storage.
      </p>
      {safe ? (
        <p className="muted small">There’s a copy elsewhere: it was uploaded to the studio or copied to a folder.</p>
      ) : (
        <div className="alert bad small">
          <Icon name="warn" size={14} /> These files haven’t been uploaded or copied anywhere. Once removed they’re gone for good.
        </div>
      )}
    </ConfirmDialog>
  );
}
